import { nextTick } from 'vue'
import { refreshFormFloatingCanSubmit } from '@/utils/formFloatingActions'

export type FormErrors = Record<string, string | undefined | null>

export interface ScrollToFirstFormErrorOptions {

  root?: HTMLElement | null

  fieldOrder?: readonly string[]

  fieldSelectorMap?: Record<string, string>

  validate?: () => boolean

  getErrors?: () => FormErrors

  _advanceOnly?: boolean
}

const HIGHLIGHT_CLASS = 'form-field-error-target'
const HIGHLIGHT_ENTER_CLASS = 'form-field-error-enter'
const HIGHLIGHT_LEAVE_CLASS = 'form-field-error-leaving'
const HIGHLIGHT_MS = 2200
const INPUT_DEBOUNCE_MS = 280
const ADVANCE_LEAVE_MS = 220
const ADVANCE_SCROLL_MS = 500
const INITIAL_SCROLL_MS = 400

interface CustomGuidedSession {
  kind: 'custom'
  abortController: AbortController
  options: ScrollToFirstFormErrorOptions
  errors: FormErrors
  advancing: boolean
  lastHighlight: HTMLElement | null
}

interface NativeGuidedSession {
  kind: 'native'
  abortController: AbortController
  form: HTMLFormElement
  advancing: boolean
  lastHighlight: HTMLElement | null
}

type GuidedSession = CustomGuidedSession | NativeGuidedSession

let guidedSession: GuidedSession | null = null

function hasAnyError(errors: FormErrors): boolean {
  return Object.values(errors).some((message) => Boolean(message))
}

function firstErrorField(
  errors: FormErrors,
  fieldOrder?: readonly string[],
): string | null {
  if (fieldOrder?.length) {
    for (const key of fieldOrder) {
      if (errors[key]) return key
    }
  }
  for (const [key, message] of Object.entries(errors)) {
    if (message) return key
  }
  return null
}

function queryFieldElement(
  root: ParentNode,
  fieldKey: string,
  fieldSelectorMap?: Record<string, string>,
): HTMLElement | null {
  const mapped = fieldSelectorMap?.[fieldKey]
  if (mapped) {
    const el = root.querySelector(mapped)
    if (el instanceof HTMLElement) return el
  }

  const selectors = [
    `[data-field="${fieldKey}"]`,
    `#${CSS.escape(fieldKey)}`,
    `[name="${fieldKey}"]`,
  ]

  for (const selector of selectors) {
    const el = root.querySelector(selector)
    if (el instanceof HTMLElement) return el
  }

  const label = root.querySelector(`label[for="${fieldKey}"]`)
  if (label) {
    const group = label.closest('.form-group, .input-field, .localized-field-group')
    const input = group?.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    )
    if (input) return input
  }

  return null
}

function resolveFieldKeyFromElement(
  el: HTMLElement,
  root: ParentNode,
  fieldSelectorMap?: Record<string, string>,
): string | null {
  const container = el.closest('[data-field]')
  if (container instanceof HTMLElement && container.dataset.field) {
    return container.dataset.field
  }

  if (el.id) return el.id

  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  ) {
    if (el.name) return el.name
  }

  if (fieldSelectorMap) {
    for (const [key, selector] of Object.entries(fieldSelectorMap)) {
      if (el.matches(selector) || el.closest(selector)) return key
    }
  }

  if (el.id) {
    const label = root.querySelector(`label[for="${CSS.escape(el.id)}"]`)
    if (label) return el.id
  }

  return null
}

function resolveScrollTarget(
  fieldEl: HTMLElement | null,
  root: ParentNode,
  fieldKey: string,
): HTMLElement | null {
  if (fieldEl) {
    const container = fieldEl.closest(
      '.form-group, .input-field, .input-wrapper, .input-group, .localized-field-group, [data-field]',
    )
    if (container instanceof HTMLElement) return container
    return fieldEl
  }

  const byDataField = root.querySelector(`[data-field="${fieldKey}"]`)
  if (byDataField instanceof HTMLElement) return byDataField

  return null
}

function focusField(fieldEl: HTMLElement | null, scrollTarget: HTMLElement): void {
  const focusable =
    fieldEl ??
    scrollTarget.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([readonly]), select, textarea, button, [tabindex]:not([tabindex="-1"])',
    )

  if (!focusable) return

  try {
    focusable.focus({ preventScroll: true })
  } catch {

  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function getScrollContainer(target: HTMLElement): Element {
  let parent = target.parentElement
  while (parent) {
    const style = window.getComputedStyle(parent)
    const overflowY = style.overflowY
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent
    }
    parent = parent.parentElement
  }
  return document.documentElement
}

function computeScrollTopToCenter(container: Element, target: HTMLElement): number {
  if (container === document.documentElement) {
    const rect = target.getBoundingClientRect()
    return window.scrollY + rect.top - window.innerHeight / 2 + rect.height / 2
  }

  const containerRect = container.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  const relativeTop = targetRect.top - containerRect.top + container.scrollTop
  return relativeTop - container.clientHeight / 2 + targetRect.height / 2
}

async function animateScrollToCenter(
  scrollTarget: HTMLElement,
  durationMs: number,
): Promise<void> {
  const container = getScrollContainer(scrollTarget)
  const destination = computeScrollTopToCenter(container, scrollTarget)
  const start =
    container === document.documentElement ? window.scrollY : container.scrollTop
  const distance = destination - start

  if (Math.abs(distance) < 4) return

  const startTime = performance.now()

  await new Promise<void>((resolve) => {
    function step(now: number) {
      const progress = Math.min((now - startTime) / durationMs, 1)
      const eased = easeInOutCubic(progress)
      const next = start + distance * eased

      if (container === document.documentElement) {
        window.scrollTo(0, next)
      } else {
        container.scrollTop = next
      }

      if (progress < 1) {
        requestAnimationFrame(step)
      } else {
        resolve()
      }
    }
    requestAnimationFrame(step)
  })
}

function clearHighlightClasses(el: HTMLElement | null): void {
  if (!el) return
  el.classList.remove(HIGHLIGHT_CLASS, HIGHLIGHT_ENTER_CLASS, HIGHLIGHT_LEAVE_CLASS)
}

async function fadeOutHighlight(el: HTMLElement | null): Promise<void> {
  if (!el) return
  el.classList.remove(HIGHLIGHT_ENTER_CLASS)
  el.classList.add(HIGHLIGHT_LEAVE_CLASS)
  await sleep(ADVANCE_LEAVE_MS)
  clearHighlightClasses(el)
}

function pulseHighlight(target: HTMLElement, isAdvance: boolean): void {
  clearHighlightClasses(target)
  void target.offsetWidth
  target.classList.add(HIGHLIGHT_CLASS)
  if (isAdvance) {
    target.classList.add(HIGHLIGHT_ENTER_CLASS)
  }
  window.setTimeout(() => {
    target.classList.remove(HIGHLIGHT_ENTER_CLASS)
  }, 380)
  window.setTimeout(() => target.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS)
}

function getLiveErrors(session: CustomGuidedSession): FormErrors {
  return session.options.getErrors?.() ?? session.errors
}

function eventTargetIsFieldControl(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(target.closest('input, select, textarea'))
}

function debounceFn<T extends (...args: never[]) => void>(
  fn: T,
  ms: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn(...args)
    }, ms)
  }
}

function stopGuidedListeners(): void {
  guidedSession?.abortController.abort()
  guidedSession = null
}

function handleAllValid(): void {
  stopGuidedListeners()
  refreshFormFloatingCanSubmit()
}

export function stopGuidedFormErrorNavigation(): void {
  stopGuidedListeners()
}

async function scrollToField(
  root: ParentNode,
  fieldKey: string,
  fieldSelectorMap?: Record<string, string>,
  isAdvance = false,
  previousHighlight: HTMLElement | null = null,
): Promise<{ scrolled: boolean; highlight: HTMLElement | null }> {
  await nextTick()
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

  const fieldEl = queryFieldElement(root, fieldKey, fieldSelectorMap)
  const scrollTarget = resolveScrollTarget(fieldEl, root, fieldKey)
  if (!scrollTarget) return { scrolled: false, highlight: null }

  if (isAdvance && previousHighlight && previousHighlight !== scrollTarget) {
    await fadeOutHighlight(previousHighlight)
    await sleep(50)
  }

  await animateScrollToCenter(
    scrollTarget,
    isAdvance ? ADVANCE_SCROLL_MS : INITIAL_SCROLL_MS,
  )

  focusField(fieldEl, scrollTarget)
  pulseHighlight(scrollTarget, isAdvance)

  return { scrolled: true, highlight: scrollTarget }
}

function startCustomGuidedNavigation(
  errors: FormErrors,
  options: ScrollToFirstFormErrorOptions,
): void {
  if (!options.validate || !options.root) return

  stopGuidedListeners()

  const abortController = new AbortController()
  const session: CustomGuidedSession = {
    kind: 'custom',
    abortController,
    options,
    errors,
    advancing: false,
    lastHighlight: null,
  }
  guidedSession = session

  const tryAdvance = async (target: EventTarget | null) => {
    const active = guidedSession
    if (!active || active.kind !== 'custom' || active.advancing) return
    if (!eventTargetIsFieldControl(target)) return

    const root = active.options.root
    if (!root) return

    const fieldKey = resolveFieldKeyFromElement(
      target,
      root,
      active.options.fieldSelectorMap,
    )
    const liveErrors = getLiveErrors(active)
    const firstError = firstErrorField(liveErrors, active.options.fieldOrder)
    if (!firstError) {
      handleAllValid()
      return
    }

    const isFirstErrorField =
      fieldKey === firstError ||
      target.closest(`[data-field="${firstError}"]`) != null ||
      Boolean(
        active.options.fieldSelectorMap?.[firstError] &&
          target.closest(active.options.fieldSelectorMap[firstError]),
      )

    if (!isFirstErrorField) return

    const previousFirst = firstError
    active.options.validate!()
    const updatedErrors = getLiveErrors(active)

    if (!hasAnyError(updatedErrors)) {
      handleAllValid()
      return
    }

    if (!updatedErrors[previousFirst]) {
      active.advancing = true
      try {
        const nextKey = firstErrorField(updatedErrors, active.options.fieldOrder)
        if (!nextKey) return

        const { highlight } = await scrollToField(
          root,
          nextKey,
          active.options.fieldSelectorMap,
          true,
          active.lastHighlight,
        )
        active.lastHighlight = highlight
      } finally {
        active.advancing = false
      }
    }
  }

  const onImmediate = (event: Event) => {
    void tryAdvance(event.target)
  }
  const onDebouncedInput = debounceFn((event: Event) => {
    void tryAdvance(event.target)
  }, INPUT_DEBOUNCE_MS)

  const { signal } = abortController
  options.root.addEventListener('blur', onImmediate, { capture: true, signal })
  options.root.addEventListener('change', onImmediate, { capture: true, signal })
  options.root.addEventListener('input', onDebouncedInput, { capture: true, signal })
}

const INVALID_FIELD_SELECTOR = 'input:invalid, select:invalid, textarea:invalid'

function startNativeGuidedNavigation(form: HTMLFormElement): void {
  stopGuidedListeners()

  const abortController = new AbortController()
  const session: NativeGuidedSession = {
    kind: 'native',
    abortController,
    form,
    advancing: false,
    lastHighlight: null,
  }
  guidedSession = session

  const tryAdvance = async (target: EventTarget | null) => {
    const active = guidedSession
    if (!active || active.kind !== 'native' || active.advancing) return
    if (!eventTargetIsFieldControl(target)) return

    const control = target.closest('input, select, textarea')
    if (
      !(
        control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement ||
        control instanceof HTMLTextAreaElement
      )
    ) {
      return
    }

    if (!control.checkValidity()) return

    await nextTick()
    if (active.form.checkValidity()) {
      handleAllValid()
      return
    }

    active.advancing = true
    try {
      await scrollToFirstNativeInvalid(active.form, {
        _advanceOnly: true,
      })
    } finally {
      active.advancing = false
    }
  }

  const onImmediate = (event: Event) => {
    void tryAdvance(event.target)
  }
  const onDebouncedInput = debounceFn((event: Event) => {
    void tryAdvance(event.target)
  }, INPUT_DEBOUNCE_MS)

  const { signal } = abortController
  form.addEventListener('blur', onImmediate, { capture: true, signal })
  form.addEventListener('change', onImmediate, { capture: true, signal })
  form.addEventListener('input', onDebouncedInput, { capture: true, signal })
}

export async function scrollToFirstFormError(
  errors: FormErrors,
  options: ScrollToFirstFormErrorOptions = {},
): Promise<boolean> {
  const fieldKey = firstErrorField(errors, options.fieldOrder)
  if (!fieldKey) {
    if (!options._advanceOnly) stopGuidedFormErrorNavigation()
    return false
  }

  refreshFormFloatingCanSubmit()

  const root = options.root ?? document
  const { scrolled, highlight } = await scrollToField(
    root,
    fieldKey,
    options.fieldSelectorMap,
    Boolean(options._advanceOnly),
    guidedSession?.kind === 'custom' ? guidedSession.lastHighlight : null,
  )

  if (guidedSession?.kind === 'custom') {
    guidedSession.lastHighlight = highlight
  }

  if (scrolled && options.validate && !options._advanceOnly) {
    startCustomGuidedNavigation(errors, options)
  }

  return scrolled
}

export interface ScrollToFirstNativeInvalidOptions {

  _advanceOnly?: boolean
}

export async function scrollToFirstNativeInvalid(
  form: HTMLElement | null,
  options: ScrollToFirstNativeInvalidOptions = {},
): Promise<boolean> {
  if (!form) return false

  await nextTick()
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

  const invalid = form.querySelector<HTMLElement>(INVALID_FIELD_SELECTOR)
  if (!invalid) {
    if (!options._advanceOnly) stopGuidedFormErrorNavigation()
    return false
  }

  if (form instanceof HTMLFormElement) {
    form.reportValidity()
  }

  const scrollTarget =
    invalid.closest(
      '.form-group, .input-field, .input-wrapper, .input-group, .localized-field-group, [data-field]',
    ) ?? invalid

  if (!(scrollTarget instanceof HTMLElement)) return false

  const previousHighlight =
    guidedSession?.kind === 'native' ? guidedSession.lastHighlight : null

  if (options._advanceOnly && previousHighlight && previousHighlight !== scrollTarget) {
    await fadeOutHighlight(previousHighlight)
    await sleep(50)
  }

  await animateScrollToCenter(
    scrollTarget,
    options._advanceOnly ? ADVANCE_SCROLL_MS : INITIAL_SCROLL_MS,
  )
  focusField(invalid, scrollTarget)
  pulseHighlight(scrollTarget, Boolean(options._advanceOnly))

  if (guidedSession?.kind === 'native') {
    guidedSession.lastHighlight = scrollTarget
  }

  if (form instanceof HTMLFormElement && !options._advanceOnly) {
    startNativeGuidedNavigation(form)
  }

  return true
}

export interface LoyaltyLevelValidationMessages {
  nameRequired: string
  nameDuplicate: string
}

export function validateLoyaltyLevelRows(
  levels: Array<{ name: string; min_points: number }>,
  messages: LoyaltyLevelValidationMessages,
): FormErrors {
  const sorted = [...levels].sort((a, b) => a.min_points - b.min_points)
  for (let i = 0; i < sorted.length; i++) {
    if (!sorted[i].name.trim()) {
      return { [`level_${i}`]: messages.nameRequired }
    }
  }
  const names = new Set(sorted.map((l) => l.name.trim().toLowerCase()))
  if (names.size !== sorted.length) {
    return { level_0: messages.nameDuplicate }
  }
  return {}
}

export async function scrollToFirstEmptyCredential(
  defs: Array<{ key: string; required?: boolean }>,
  credentials: Record<string, unknown>,
  root?: HTMLElement | null,
): Promise<boolean> {
  const credentialErrors: FormErrors = {}

  const buildCredentialErrors = (): FormErrors => {
    const errs: FormErrors = {}
    for (const field of defs) {
      if (field.required === false) continue
      if (!String(credentials[field.key] ?? '').trim()) {
        errs[field.key] = ' '
      }
    }
    return errs
  }

  const syncCredentialErrors = (): boolean => {
    const fresh = buildCredentialErrors()
    for (const key of Object.keys(credentialErrors)) {
      if (!fresh[key]) credentialErrors[key] = ''
    }
    for (const [key, message] of Object.entries(fresh)) {
      credentialErrors[key] = message
    }
    return !hasAnyError(credentialErrors)
  }

  if (syncCredentialErrors()) return false

  return scrollToFirstFormError(credentialErrors, {
    root: root ?? document.body,
    fieldOrder: defs.map((d) => d.key),
    fieldSelectorMap: Object.fromEntries(
      defs.map((d) => [d.key, `[data-field="${d.key}"]`]),
    ),
    validate: syncCredentialErrors,
    getErrors: () => credentialErrors,
  })
}
